import React, { useEffect, useState, useRef } from "react";
import Switch from "react-switch";
import axiosInstance from "../../config/axiosInterceptor";
import { toast } from "react-toastify";

// Animated counter hook
const useAnimatedCounter = (target, speed = 1200) => {
  const [count, setCount] = useState(0);
  const raf = useRef();
  useEffect(() => {
    if (target == null || isNaN(target)) return setCount(0);
    let startTime = null;
    const duration = speed;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const current = Math.floor(progress * target);
      setCount(current);
      if (progress < 1) raf.current = requestAnimationFrame(animate);
      else setCount(target);
    };
    raf.current = requestAnimationFrame(animate);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, speed]);
  return count;
};

const Balance = () => {
  const baseUrl = process.env.REACT_APP_BASEURL5;
  const [balance, setBalance] = useState([{ coin: 0, recievingCoins: 0, recievingCoinsAbove5000: 0 }]);
  const [withdrawalStatus, setWithdrawalStatus] = useState({
    coinsWithdrawal: "0",
    familyWithdrawal: "0",
  });
  const [withdrawalValues, setWithdrawalValues] = useState({
    USD: "",
    USDINR: "",
    agencyComm: "",
    familyComm: "",
    agencyAdminComm: "",
    minWithdrawlCoin: "",
  });
  const [isSavingValues, setIsSavingValues] = useState(false);

  // --- Video Wallet State ---
  const [videoWalletValues, setVideoWalletValues] = useState({
    videoWatchTimeEligibility: "",
    likeCount: "",
    likeCountAmount: "",
    viewCount: "",
    viewCountAmount: "",
    shareCount: "",
    shareCountAmount: "",
    MCoinsNeeded: "",
  });
  const [isSavingVideoWallet, setIsSavingVideoWallet] = useState(false);
  const [videoWalletPdfPath, setVideoWalletPdfPath] = useState("");
  const [videoWalletToggles, setVideoWalletToggles] = useState({
    watchTimeEnabled: true,
    likeWalletOn:     true,
    shareWalletOn:    true,
    viewWalletOn:     true,
  });
  const [togglingField, setTogglingField] = useState(null);

  // --- Animated counters ---
  const animatedCoin = useAnimatedCounter(balance[0]?.coin || 0, 1200);
  const animatedRecievingCoins = useAnimatedCounter(balance[0]?.recievingCoins || 0, 1200);
  const animatedRecievingCoinsAbove = useAnimatedCounter(balance[0]?.recievingCoinsAbove5000 || 0, 1200);

  useEffect(() => {
    const fetchBalance = async () => {
      const response = await axiosInstance.get(baseUrl + '/admin/getBalance');
      if (response?.data.succes === "1") setBalance(response?.data.details);
    };
    const fetchWithdrawalStatus = async () => {
      const response = await fetch(process.env.REACT_APP_BASEURL1 + '/admin/withdrawal/checkStatus');
      const data = await response.json();
      if (data.success == "1") {
        const details = data.details?.[0] || {};
        const value = details.value || {};
        setWithdrawalStatus({
          coinsWithdrawal: details.coinsWithdrawal || "0",
          familyWithdrawal: details.familyWithdrawal || "0",
        });
        setWithdrawalValues({
          USD: value.USD ?? "",
          USDINR: value.USDINR ?? "",
          agencyComm: value.agencyComm ?? "",
          familyComm: value.familyComm ?? "",
          agencyAdminComm: value.agencyAdminComm ?? "",
          minWithdrawlCoin: value.minWithdrawlCoin ?? "",
        });
      }
    };
    fetchBalance();
    fetchWithdrawalStatus();

    const fetchVideoWalletSettings = async () => {
      try {
        const r = await axiosInstance.get(`${process.env.REACT_APP_BASEURL1}/admin/withdrawal/videoWallet/get`);
        if (r?.data?.success === "1") {
          const vw = r.data.videoWallet || {};
          setVideoWalletValues({
            videoWatchTimeEligibility: vw.videoWatchTimeEligibility ?? "",
            likeCount: vw.likeCount ?? "",
            likeCountAmount: vw.likeCountAmount ?? "",
            viewCount: vw.viewCount ?? "",
            viewCountAmount: vw.viewCountAmount ?? "",
            shareCount: vw.shareCount ?? "",
            shareCountAmount: vw.shareCountAmount ?? "",
            MCoinsNeeded: vw.MCoinsNeeded ?? "",
          });
          setVideoWalletToggles({
            watchTimeEnabled: vw.watchTimeEnabled !== false,
            likeWalletOn:     vw.likeWalletOn     !== false,
            shareWalletOn:    vw.shareWalletOn    !== false,
            viewWalletOn:     vw.viewWalletOn     !== false,
          });
          if (r.data.videoWalletPdfPath) setVideoWalletPdfPath(r.data.videoWalletPdfPath);
        }
      } catch (_) {}
    };
    fetchVideoWalletSettings();
  }, [baseUrl]);

  const toggleWithdrawal = async (type) => {
    try {
      const toggleType = type === "1" ? "Coins Withdrawal" : "Family Withdrawal";
      const confirmToggle = window.confirm(`Are you sure you want to toggle ${toggleType}?`);
      if (confirmToggle) {
        const response = await axiosInstance.post(`${process.env.REACT_APP_BASEURL1}/admin/withdrawal/updateWithdrawal?type=${type}`);
        if (response.data.success === "1") {
          const updatedStatus = response.data.result;
          setWithdrawalStatus({
            coinsWithdrawal: updatedStatus.coinsWithdrawal,
            familyWithdrawal: updatedStatus.familyWithdrawal,
          });
        }
      }
    } catch (error) {
      toast.error(error.message)
    }
  };

  const handleValueChange = (field, value) => {
    setWithdrawalValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleVideoWalletChange = (field, value) => {
    setVideoWalletValues((prev) => ({ ...prev, [field]: value }));
  };

  const saveVideoWalletSettings = async () => {
    try {
      if (!window.confirm("Update Video Wallet settings?")) return;
      setIsSavingVideoWallet(true);
      const payload = {
        videoWatchTimeEligibility: Number(videoWalletValues.videoWatchTimeEligibility),
        likeCount:        Number(videoWalletValues.likeCount),
        likeCountAmount:  Number(videoWalletValues.likeCountAmount),
        viewCount:        Number(videoWalletValues.viewCount),
        viewCountAmount:  Number(videoWalletValues.viewCountAmount),
        shareCount:       Number(videoWalletValues.shareCount),
        shareCountAmount: Number(videoWalletValues.shareCountAmount),
        MCoinsNeeded:     Number(videoWalletValues.MCoinsNeeded),
      };
      if (Object.values(payload).some((v) => Number.isNaN(v))) {
        toast.error("Please enter valid numeric values");
        setIsSavingVideoWallet(false);
        return;
      }
      const r = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/videoWallet/update`,
        payload
      );
      if (r?.data?.success === "1") {
        const vw = r.data.videoWallet || {};
        setVideoWalletValues({
          videoWatchTimeEligibility: vw.videoWatchTimeEligibility ?? "",
          likeCount: vw.likeCount ?? "",
          likeCountAmount: vw.likeCountAmount ?? "",
          viewCount: vw.viewCount ?? "",
          viewCountAmount: vw.viewCountAmount ?? "",
          shareCount: vw.shareCount ?? "",
          shareCountAmount: vw.shareCountAmount ?? "",
          MCoinsNeeded: vw.MCoinsNeeded ?? "",
        });
        setVideoWalletToggles({
          watchTimeEnabled: vw.watchTimeEnabled !== false,
          likeWalletOn:     vw.likeWalletOn     !== false,
          shareWalletOn:    vw.shareWalletOn    !== false,
          viewWalletOn:     vw.viewWalletOn     !== false,
        });
        if (r.data.videoWalletPdfPath) setVideoWalletPdfPath(r.data.videoWalletPdfPath);
        toast.success("Video Wallet settings updated");
      } else {
        toast.error(r?.data?.message || "Failed to update");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSavingVideoWallet(false);
    }
  };

  const toggleVideoWalletField = async (field) => {
    const labels = {
      watchTimeEnabled: "Video Wallet (master)",
      likeWalletOn:     "Like Wallet",
      shareWalletOn:    "Share Wallet",
      viewWalletOn:     "View Wallet",
    };
    if (!window.confirm(`Toggle ${labels[field]}?`)) return;
    try {
      setTogglingField(field);
      const r = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/videoWallet/toggle`,
        { field }
      );
      if (r?.data?.success === "1") {
        const vw = r.data.videoWallet || {};
        setVideoWalletToggles({
          watchTimeEnabled: vw.watchTimeEnabled !== false,
          likeWalletOn:     vw.likeWalletOn     !== false,
          shareWalletOn:    vw.shareWalletOn    !== false,
          viewWalletOn:     vw.viewWalletOn     !== false,
        });
        toast.success(`${labels[field]} toggled`);
      } else {
        toast.error(r?.data?.message || "Toggle failed");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTogglingField(null);
    }
  };

  const saveWithdrawalValues = async () => {
    try {
      const confirmSave = window.confirm("Are you sure you want to update withdrawal values?");
      if (!confirmSave) return;

      setIsSavingValues(true);
      const payload = {
        USD: Number(withdrawalValues.USD),
        USDINR: Number(withdrawalValues.USDINR),
        agencyComm: Number(withdrawalValues.agencyComm),
        familyComm: Number(withdrawalValues.familyComm),
        agencyAdminComm: Number(withdrawalValues.agencyAdminComm),
        minWithdrawlCoin: Number(withdrawalValues.minWithdrawlCoin),
      };

      if (Object.values(payload).some((v) => Number.isNaN(v))) {
        toast.error("Please enter valid numeric values");
        setIsSavingValues(false);
        return;
      }

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/updateValues`,
        payload
      );

      if (response?.data?.success === "1") {
        const updatedValue = response?.data?.result?.value || {};
        setWithdrawalValues({
          USD: updatedValue.USD ?? "",
          USDINR: updatedValue.USDINR ?? "",
          agencyComm: updatedValue.agencyComm ?? "",
          familyComm: updatedValue.familyComm ?? "",
          agencyAdminComm: updatedValue.agencyAdminComm ?? "",
          minWithdrawlCoin: updatedValue.minWithdrawlCoin ?? "",
        });
        toast.success("Withdrawal values updated successfully");
      } else {
        toast.error(response?.data?.message || "Failed to update values");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSavingValues(false);
    }
  };

  // --- Responsive & Unique styles injected ---
  useEffect(() => {
    if (!document.getElementById("balance-unique-styles")) {
      const style = document.createElement("style");
      style.id = "balance-unique-styles";
      style.innerHTML = `
        .balance-unique-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 18px;
        }
        .balance-unique-card {
          border-radius: 18px;
          min-height: 120px;
          box-shadow: 0 3px 18px #23232310;
          padding: 1.4rem 1.2rem;
          display: flex;
          align-items: center;
          animation: fadeInUpB .7s cubic-bezier(.7,0,.3,1);
        }
        .balance-unique-blue { background: linear-gradient(93deg,#1e7ebf 65%,#1ccfff 100%);}
        .balance-unique-green { background: linear-gradient(93deg,#24742c 45%,#70e000 100%);}
        .balance-unique-purple { background: linear-gradient(93deg,#7e2ca2 35%,#ce66fa 100%);}
        .balance-unique-card-icon {
          font-size: 2.1rem;
          width: 55px;
          height: 55px;
          border-radius: 13px;
          margin-right: 1.3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 1px 5px #00000014;
        }
        .balance-unique-card-title {
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: .01em;
          color: #fff;
          margin-bottom: .32em;
        }
        .balance-unique-card-number {
          font-weight: 800;
          font-size: 2.2rem;
          color: #fff;
          word-break: break-all;
          line-height: 1.07;
          text-shadow: 0 2px 9px #00000018;
          letter-spacing: 0.02em;
        }
        .balance-unique-switch-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 22px;
        }
        .balance-unique-switch-card {
          background: #fff;
          border-radius: 18px;
          padding: 22px 22px;
          min-height: 90px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          box-shadow: 0 3px 20px #23232310;
        }
        .balance-unique-switch-label {
          font-weight: 700;
          font-size: 1.08rem;
          margin-bottom: 0.8em;
          color: #233;
        }
        .balance-unique-values-card {
          margin-top: 20px;
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 3px 20px #23232310;
          padding: 22px;
        }
        .balance-unique-values-title {
          font-size: 1.12rem;
          font-weight: 700;
          margin-bottom: 14px;
          color: #1f2f4a;
        }
        .balance-unique-values-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }
        .balance-unique-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .balance-unique-field label {
          font-weight: 600;
          color: #334;
          font-size: 0.92rem;
        }
        .balance-unique-field input {
          border: 1px solid #d9dce2;
          border-radius: 8px;
          padding: 8px 10px;
          font-size: 0.95rem;
          outline: none;
        }
        .balance-unique-save-btn {
          margin-top: 14px;
          background: #1e7ebf;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 9px 16px;
          font-weight: 700;
          cursor: pointer;
        }
        .balance-unique-save-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        @media (max-width: 1200px) {
          .balance-unique-grid { grid-template-columns: 1fr 1fr; }
          .balance-unique-values-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 900px) {
          .balance-unique-grid { grid-template-columns: 1fr; gap: 14px;}
          .balance-unique-switch-row { grid-template-columns: 1fr; gap: 14px;}
          .balance-unique-card { padding: 1.1rem 0.7rem; }
          .balance-unique-values-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .balance-unique-card-title { font-size: .92rem;}
          .balance-unique-card-number { font-size: 1.3rem;}
          .balance-unique-card-icon { width: 35px;height: 35px;font-size:1.2rem;}
        }
        @keyframes fadeInUpB { 0% {opacity:0;transform:translateY(36px);} 60%{opacity:1;transform:translateY(-8px);} 100%{opacity:1;transform:translateY(0);} }
        .buvw-card { margin-top:24px;background:#fff;border-radius:18px;box-shadow:0 3px 20px #23232310;padding:24px; }
        .buvw-title { font-size:1.12rem;font-weight:700;color:#4b2aad;margin-bottom:16px;display:flex;align-items:center;gap:8px; }
        .buvw-toggles-row { display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:18px; }
        .buvw-toggle-card { background:#f8f5ff;border:1.5px solid #e0d6ff;border-radius:12px;padding:14px 16px;display:flex;flex-direction:column;gap:8px; }
        .buvw-toggle-card.master { background:#ede9ff;border-color:#b39ddb; }
        .buvw-toggle-card.disabled-type { opacity:0.52;pointer-events:none; }
        .buvw-toggle-label { font-weight:700;font-size:0.9rem;color:#4b2aad; }
        .buvw-toggle-status { font-size:0.8rem;font-weight:600; }
        .buvw-body-blur { opacity:0.35;pointer-events:none;user-select:none;filter:blur(1.5px);transition:all 0.25s; }
        .buvw-grid { display:grid;grid-template-columns:repeat(3,1fr);gap:14px; }
        .buvw-field { display:flex;flex-direction:column;gap:5px; }
        .buvw-field label { font-weight:600;font-size:0.9rem;color:#334; }
        .buvw-field label span.buvw-unit { font-weight:400;color:#888;font-size:0.82rem;margin-left:4px; }
        .buvw-field input { border:1.5px solid #d9dce2;border-radius:8px;padding:8px 10px;font-size:0.95rem;outline:none;transition:border 0.18s; }
        .buvw-field input:focus { border-color:#7c3aed;box-shadow:0 0 0 3px #7c3aed18; }
        .buvw-master-row { display:flex;margin-bottom:18px; }
        .buvw-master-row .buvw-toggle-card { min-width:180px; }
        .buvw-section-header { display:flex;align-items:center;justify-content:space-between;margin:18px 0 8px;border-bottom:1.5px solid #ede9ff;padding-bottom:6px; }
        .buvw-inline-status { font-size:0.75rem;font-weight:700;margin-left:10px;padding:2px 8px;border-radius:20px;background:#f3f4f6; }
        .buvw-section-label { font-size:0.82rem;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#7c3aed;margin:18px 0 8px;border-bottom:1.5px solid #ede9ff;padding-bottom:4px; }
        .buvw-actions { display:flex;align-items:center;gap:12px;margin-top:18px;flex-wrap:wrap; }
        .buvw-save-btn { background:linear-gradient(90deg,#7c3aed,#a855f7);color:#fff;border:none;border-radius:9px;padding:10px 20px;font-weight:700;font-size:0.97rem;cursor:pointer;display:flex;align-items:center;gap:7px; }
        .buvw-save-btn:disabled { opacity:0.65;cursor:not-allowed; }
        .buvw-pdf-btn { background:#fff;color:#7c3aed;border:2px solid #7c3aed;border-radius:9px;padding:9px 18px;font-weight:700;font-size:0.95rem;text-decoration:none;display:flex;align-items:center;gap:7px; }
        .buvw-rule-note { margin-top:14px;padding:10px 14px;background:#f5f0ff;border-left:3px solid #7c3aed;border-radius:0 8px 8px 0;font-size:0.88rem;color:#4b2aad;line-height:1.55; }
        @media(max-width:1100px){.buvw-toggles-row{grid-template-columns:1fr 1fr;}.buvw-grid{grid-template-columns:1fr 1fr;}}
        @media(max-width:600px){.buvw-toggles-row{grid-template-columns:1fr;}.buvw-grid{grid-template-columns:1fr;}}
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <>
      <section className="content-header">
        <h1 style={{ fontWeight: 800, color: "#1e7ebf", fontSize: "2.1rem" }}>Balance</h1>
        <ol className="breadcrumb" style={{ background: "none", marginBottom: 0 }}>
          <li>
            <a href="#" style={{ color: "#1e7ebf" }}>Home</a>
          </li>
          <li className="active" style={{ color: "#777" }}>Balance</li>
        </ol>
      </section>
      <section className="content">
        <span className="bg-warning" style={{
          padding: "7px 13px",
          background: "#fffbe7",
          color: "#a77c1d",
          borderRadius: 7,
          marginBottom: 14,
          fontWeight: 600,
          display: "inline-block"
        }}>Note : Test Ids are excluded</span>
        <div className="balance-unique-grid" style={{ marginTop: "21px" }}>
          {/* Total Stars */}
          <div className="balance-unique-card balance-unique-blue">
            <div className="balance-unique-card-icon" style={{ background: "#187db6" }}>
              <i className="fa-solid fa-star" style={{ color: "#ffeb3b" }}></i>
            </div>
            <div>
              <div className="balance-unique-card-title">TOTAL STARS</div>
              <div className="balance-unique-card-number">{animatedCoin.toLocaleString()}</div>
            </div>
          </div>
          {/* Total M.Coins */}
          <div className="balance-unique-card balance-unique-green">
            <div className="balance-unique-card-icon" style={{ background: "#378d13" }}>
              <i className="fa-solid fa-coins" style={{ color: "#b2ff59" }}></i>
            </div>
            <div>
              <div className="balance-unique-card-title">TOTAL M.COINS</div>
              <div className="balance-unique-card-number">{animatedRecievingCoins.toLocaleString()}</div>
            </div>
          </div>
          {/* M.Coins > 4999 */}
          <div className="balance-unique-card balance-unique-purple">
            <div className="balance-unique-card-icon" style={{ background: "#842aaf" }}>
              <i className="fa-solid fa-gem" style={{ color: "#fff6fa" }}></i>
            </div>
            <div>
              <div className="balance-unique-card-title">M.COINS &gt; 4999</div>
              <div className="balance-unique-card-number">{animatedRecievingCoinsAbove.toLocaleString()}</div>
            </div>
          </div>
        </div>
        {/* Withdrawal Switches */}
        <div className="balance-unique-switch-row">
          <div className="balance-unique-switch-card">
            <span className="balance-unique-switch-label">
              COINS WITHDRAWAL STATUS:{" "}
              {withdrawalStatus.coinsWithdrawal === "1" ? (
                <span style={{ color: "#24a34d" }}>ENABLED</span>
              ) : (
                <span style={{ color: "#d03b23" }}>DISABLED</span>
              )}
            </span>
            <Switch
              checked={withdrawalStatus.coinsWithdrawal === "1"}
              onChange={() => toggleWithdrawal("1")}
              onColor="#24a34d"
              offColor="#d03b23"
              uncheckedIcon={false}
              checkedIcon={false}
              height={26}
              width={50}
              handleDiameter={24}
            />
          </div>
          <div className="balance-unique-switch-card">
            <span className="balance-unique-switch-label">
              FAMILY WITHDRAWAL STATUS:{" "}
              {withdrawalStatus.familyWithdrawal === "1" ? (
                <span style={{ color: "#3545c7" }}>ENABLED</span>
              ) : (
                <span style={{ color: "#d03b23" }}>DISABLED</span>
              )}
            </span>
            <Switch
              checked={withdrawalStatus.familyWithdrawal === "1"}
              onChange={() => toggleWithdrawal("0")}
              onColor="#3545c7"
              offColor="#d03b23"
              uncheckedIcon={false}
              checkedIcon={false}
              height={26}
              width={50}
              handleDiameter={24}
            />
          </div>
        </div>

        <div className="balance-unique-values-card">
          <div className="balance-unique-values-title">Withdrawal Value Settings</div>
          <div className="balance-unique-values-grid">
            <div className="balance-unique-field">
              <label>USD to Mcoin</label>
              <input
                type="number"
                value={withdrawalValues.USD}
                onChange={(e) => handleValueChange("USD", e.target.value)}
              />
            </div>
            <div className="balance-unique-field">
              <label>USD to INR</label>
              <input
                type="number"
                value={withdrawalValues.USDINR}
                onChange={(e) => handleValueChange("USDINR", e.target.value)}
              />
            </div>
            <div className="balance-unique-field">
              <label>Agency Comm</label>
              <input
                type="number"
                step="0.01"
                value={withdrawalValues.agencyComm}
                onChange={(e) => handleValueChange("agencyComm", e.target.value)}
              />
            </div>
            <div className="balance-unique-field">
              <label>Family Comm</label>
              <input
                type="number"
                step="0.01"
                value={withdrawalValues.familyComm}
                onChange={(e) => handleValueChange("familyComm", e.target.value)}
              />
            </div>
            <div className="balance-unique-field">
              <label>Agency Admin Comm</label>
              <input
                type="number"
                step="0.01"
                value={withdrawalValues.agencyAdminComm}
                onChange={(e) => handleValueChange("agencyAdminComm", e.target.value)}
              />
            </div>
            <div className="balance-unique-field">
              <label>Min Withdrawl Coin</label>
              <input
                type="number"
                value={withdrawalValues.minWithdrawlCoin}
                onChange={(e) => handleValueChange("minWithdrawlCoin", e.target.value)}
              />
            </div>
          </div>
          <button
            className="balance-unique-save-btn"
            onClick={saveWithdrawalValues}
            disabled={isSavingValues}
          >
            {isSavingValues ? "Saving..." : "Save Settings"}
          </button>
        </div>

        {/* ── Video Wallet Settings ── */}
        <div className="buvw-card">
          <div className="buvw-title">
            <i className="fa-solid fa-wallet"></i> Video Wallet Reward Settings
          </div>

          {/* ── Master toggle ── */}
          <div className="buvw-master-row">
            <div className="buvw-toggle-card master">
              <span className="buvw-toggle-label"><i className="fa-solid fa-power-off" style={{ marginRight: 5 }}></i>Video Wallet</span>
              <span className="buvw-toggle-status" style={{ color: videoWalletToggles.watchTimeEnabled ? "#24a34d" : "#d03b23" }}>
                {videoWalletToggles.watchTimeEnabled ? "ENABLED" : "DISABLED"}
              </span>
              <Switch
                checked={videoWalletToggles.watchTimeEnabled}
                onChange={() => toggleVideoWalletField("watchTimeEnabled")}
                onColor="#24a34d" offColor="#d03b23"
                uncheckedIcon={false} checkedIcon={false}
                height={24} width={46} handleDiameter={22}
                disabled={togglingField === "watchTimeEnabled"}
              />
            </div>
          </div>

          {/* Body — blurred when master is off */}
          <div className={videoWalletToggles.watchTimeEnabled ? "" : "buvw-body-blur"}>
            <div className="buvw-rule-note">
              <strong>Rules:</strong> User must reach the minimum threshold count to collect. Each collect awards exactly
              the configured M-Coins for that type (1 batch). Any remaining count above the threshold is kept and carried
              forward. Only one collect per type per day is allowed.
            </div>

            {/* ── Eligibility ── */}
            <div className="buvw-section-label">Eligibility</div>
            <div className="buvw-grid">
              <div className="buvw-field">
                <label>Watch Time Eligibility <span className="buvw-unit">(total minutes required)</span></label>
                <input type="number" min="0" step="1"
                  value={videoWalletValues.videoWatchTimeEligibility}
                  onChange={(e) => handleVideoWalletChange("videoWatchTimeEligibility", e.target.value)}
                  placeholder="e.g. 60" />
              </div>
              <div className="buvw-field">
                <label>Min M-Coins to Transfer <span className="buvw-unit">(wallet threshold)</span></label>
                <input type="number" min="0" step="1"
                  value={videoWalletValues.MCoinsNeeded}
                  onChange={(e) => handleVideoWalletChange("MCoinsNeeded", e.target.value)}
                  placeholder="e.g. 100" />
              </div>
            </div>

            {/* ── Like Wallet ── */}
            <div className="buvw-section-header">
              <div className="buvw-section-label" style={{ color: "#e03b5e", margin: 0 }}>
                <i className="fa-solid fa-heart" style={{ marginRight: 6 }}></i>Like Wallet
                <span className="buvw-inline-status" style={{ color: videoWalletToggles.likeWalletOn ? "#24a34d" : "#d03b23" }}>
                  {videoWalletToggles.likeWalletOn ? "ENABLED" : "DISABLED"}
                </span>
              </div>
              <Switch
                checked={videoWalletToggles.likeWalletOn}
                onChange={() => toggleVideoWalletField("likeWalletOn")}
                onColor="#24a34d" offColor="#d03b23"
                uncheckedIcon={false} checkedIcon={false}
                height={22} width={42} handleDiameter={20}
                disabled={togglingField === "likeWalletOn" || !videoWalletToggles.watchTimeEnabled}
              />
            </div>
            <div className={(!videoWalletToggles.likeWalletOn || !videoWalletToggles.watchTimeEnabled) ? "buvw-body-blur" : ""}>
              <div className="buvw-grid">
                <div className="buvw-field">
                  <label>Like M-Coins Threshold <span className="buvw-unit">(min mcoins to collect)</span></label>
                  <input type="number" min="0" step="1"
                    value={videoWalletValues.likeCount}
                    onChange={(e) => handleVideoWalletChange("likeCount", e.target.value)}
                    placeholder="e.g. 20" />
                </div>
                <div className="buvw-field">
                  <label>Like Reward <span className="buvw-unit">(M-Coins per like)</span></label>
                  <input type="number" min="0" step="0.01"
                    value={videoWalletValues.likeCountAmount}
                    onChange={(e) => handleVideoWalletChange("likeCountAmount", e.target.value)}
                    placeholder="e.g. 40" />
                </div>
              </div>
            </div>

            {/* ── Share Wallet ── */}
            <div className="buvw-section-header">
              <div className="buvw-section-label" style={{ color: "#3b82f6", margin: 0 }}>
                <i className="fa-solid fa-share-nodes" style={{ marginRight: 6 }}></i>Share Wallet
                <span className="buvw-inline-status" style={{ color: videoWalletToggles.shareWalletOn ? "#24a34d" : "#d03b23" }}>
                  {videoWalletToggles.shareWalletOn ? "ENABLED" : "DISABLED"}
                </span>
              </div>
              <Switch
                checked={videoWalletToggles.shareWalletOn}
                onChange={() => toggleVideoWalletField("shareWalletOn")}
                onColor="#24a34d" offColor="#d03b23"
                uncheckedIcon={false} checkedIcon={false}
                height={22} width={42} handleDiameter={20}
                disabled={togglingField === "shareWalletOn" || !videoWalletToggles.watchTimeEnabled}
              />
            </div>
            <div className={(!videoWalletToggles.shareWalletOn || !videoWalletToggles.watchTimeEnabled) ? "buvw-body-blur" : ""}>
              <div className="buvw-grid">
                <div className="buvw-field">
                  <label>Share M-Coins Threshold <span className="buvw-unit">(min mcoins to collect)</span></label>
                  <input type="number" min="0" step="1"
                    value={videoWalletValues.shareCount}
                    onChange={(e) => handleVideoWalletChange("shareCount", e.target.value)}
                    placeholder="e.g. 10" />
                </div>
                <div className="buvw-field">
                  <label>Share Reward <span className="buvw-unit">(M-Coins per share)</span></label>
                  <input type="number" min="0" step="0.01"
                    value={videoWalletValues.shareCountAmount}
                    onChange={(e) => handleVideoWalletChange("shareCountAmount", e.target.value)}
                    placeholder="e.g. 30" />
                </div>
              </div>
            </div>

            {/* ── View Wallet ── */}
            <div className="buvw-section-header">
              <div className="buvw-section-label" style={{ color: "#f59e0b", margin: 0 }}>
                <i className="fa-solid fa-eye" style={{ marginRight: 6 }}></i>View Wallet
                <span className="buvw-inline-status" style={{ color: videoWalletToggles.viewWalletOn ? "#24a34d" : "#d03b23" }}>
                  {videoWalletToggles.viewWalletOn ? "ENABLED" : "DISABLED"}
                </span>
              </div>
              <Switch
                checked={videoWalletToggles.viewWalletOn}
                onChange={() => toggleVideoWalletField("viewWalletOn")}
                onColor="#24a34d" offColor="#d03b23"
                uncheckedIcon={false} checkedIcon={false}
                height={22} width={42} handleDiameter={20}
                disabled={togglingField === "viewWalletOn" || !videoWalletToggles.watchTimeEnabled}
              />
            </div>
            <div className={(!videoWalletToggles.viewWalletOn || !videoWalletToggles.watchTimeEnabled) ? "buvw-body-blur" : ""}>
              <div className="buvw-grid">
                <div className="buvw-field">
                  <label>View M-Coins Threshold <span className="buvw-unit">(min mcoins to collect)</span></label>
                  <input type="number" min="0" step="1"
                    value={videoWalletValues.viewCount}
                    onChange={(e) => handleVideoWalletChange("viewCount", e.target.value)}
                    placeholder="e.g. 100" />
                </div>
                <div className="buvw-field">
                  <label>View Reward <span className="buvw-unit">(M-Coins per view)</span></label>
                  <input type="number" min="0" step="0.01"
                    value={videoWalletValues.viewCountAmount}
                    onChange={(e) => handleVideoWalletChange("viewCountAmount", e.target.value)}
                    placeholder="e.g. 50" />
                </div>
              </div>
            </div>

            <div className="buvw-actions">
              <button className="buvw-save-btn" onClick={saveVideoWalletSettings} disabled={isSavingVideoWallet}>
                <i className="fa-solid fa-floppy-disk"></i>
                {isSavingVideoWallet ? "Saving..." : "Save & Generate PDF"}
              </button>
              {videoWalletPdfPath && (
                <a
                  href={`${process.env.REACT_APP_BASEURL1}/${videoWalletPdfPath}`}
                  target="_blank" rel="noopener noreferrer"
                  className="buvw-pdf-btn"
                >
                  <i className="fa-solid fa-file-pdf"></i> View Rules PDF
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Balance;
